/**
 * Hash-based router for the Mochia SPA.
 *
 * Why hash-based: GitHub Pages / static hosts don't need a server-side
 * SPA fallback if the active path lives in `location.hash`. Browser
 * back/forward also Just Works because each `navigate()` triggers a
 * `hashchange` event we listen to globally.
 *
 * Sharp edges:
 *   - Malformed hashes (no leading `#/`, garbage characters) collapse to
 *     `#/home` so the app never has to render an unknown screen.
 *   - SSR / Node callers: returns the default route when `window` is
 *     undefined.
 */
import { useCallback, useEffect, useState } from 'react';

export type RoutePath =
  | '/home'
  | '/all-tx'
  | '/analytics'
  | '/recur'
  | '/cats'
  | '/search'
  | '/notif'
  | '/settings'
  | '/settings/books'
  | '/settings/cats'
  | '/settings/sync'
  | '/settings/notif'
  | '/settings/account'
  | '/login';

export const VALID_ROUTES: ReadonlySet<RoutePath> = new Set<RoutePath>([
  '/home',
  '/all-tx',
  '/analytics',
  '/recur',
  '/cats',
  '/search',
  '/notif',
  '/settings',
  '/settings/books',
  '/settings/cats',
  '/settings/sync',
  '/settings/notif',
  '/settings/account',
  '/login',
]);

export const DEFAULT_ROUTE: RoutePath = '/home';

interface ParsedRoute {
  path: RoutePath;
  params: Record<string, string>;
}

/**
 * Parse `location.hash` into a known route path and a query-param map.
 *
 * Accepts both `#/home` and `#/home?foo=bar`. Anything we don't recognize
 * collapses to the default route — we never render an unknown view.
 */
export function parseHash(hash: string): ParsedRoute {
  if (!hash || !hash.startsWith('#/')) {
    return { path: DEFAULT_ROUTE, params: {} };
  }
  const raw = hash.slice(1); // strip leading '#'
  const [pathPart, queryPart] = raw.split('?', 2);
  const candidate = (pathPart ?? '').replace(/\/+$/, '') || DEFAULT_ROUTE;

  const path = (VALID_ROUTES.has(candidate as RoutePath)
    ? candidate
    : DEFAULT_ROUTE) as RoutePath;

  const params: Record<string, string> = {};
  if (queryPart) {
    for (const pair of queryPart.split('&')) {
      if (!pair) continue;
      const [k, v = ''] = pair.split('=', 2);
      if (k) params[decodeURIComponent(k)] = decodeURIComponent(v);
    }
  }
  return { path, params };
}

const readCurrentHash = (): string => {
  if (typeof window === 'undefined') return '';
  return window.location.hash;
};

/**
 * Navigate to a route. Updates `location.hash` which fires `hashchange`
 * automatically — we don't need to dispatch our own event.
 */
export function navigate(to: RoutePath, params?: Record<string, string>): void {
  if (typeof window === 'undefined') return;
  let target = `#${to}`;
  if (params && Object.keys(params).length > 0) {
    const qs = Object.entries(params)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    target += `?${qs}`;
  }
  if (window.location.hash === target) return;
  window.location.hash = target;
}

export interface RouteState extends ParsedRoute {
  navigate: (to: RoutePath, params?: Record<string, string>) => void;
}

export function useRoute(): RouteState {
  const [parsed, setParsed] = useState<ParsedRoute>(() =>
    parseHash(readCurrentHash()),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onChange = (): void => {
      setParsed(parseHash(readCurrentHash()));
    };

    // If we landed on an empty hash (e.g. fresh `/` load), normalize so
    // the URL bar reflects the actual route.
    if (!readCurrentHash()) {
      window.location.replace(`${window.location.pathname}${window.location.search}#${DEFAULT_ROUTE}`);
    }

    window.addEventListener('hashchange', onChange);
    window.addEventListener('popstate', onChange);
    return () => {
      window.removeEventListener('hashchange', onChange);
      window.removeEventListener('popstate', onChange);
    };
  }, []);

  const nav = useCallback((to: RoutePath, params?: Record<string, string>) => {
    navigate(to, params);
  }, []);

  return { path: parsed.path, params: parsed.params, navigate: nav };
}
